import { 
  getFirestore, 
  doc, 
  updateDoc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  deleteObject 
} from 'firebase/storage';

/**
 * Service for handling onboarding test flow data reset functionality
 */

/**
 * Returns the initial/default onboarding data structure
 * @returns {Object} Initial onboarding data state
 */
export const getInitialOnboardingState = () => {
  return {
    firstName: '',
    lastName: '',
    linkedinUrl: '',
    currentRole: '',
    companyId: '',
    companyName: '',
    profilePicture: null,
    location: '',
    locationData: null, // Structured location data from Google Places
    jobSearchStatus: ''
  };
};

/**
 * Clears user onboarding data from Firebase user profile
 * @param {string} userId - The user's Firebase UID
 * @returns {Promise<{success: boolean, error?: string}>} Result of the operation
 */
export const clearUserOnboardingData = async (userId) => {
  try {
    console.log(`[OnboardingTest] Starting data reset for user: ${userId}`);
    
    if (!userId) {
      throw new Error('User ID is required');
    }

    const db = getFirestore();
    const storage = getStorage();
    const profileRef = doc(db, 'user_profiles', userId);
    
    // First, get the current profile to check for profile picture
    console.log('[OnboardingTest] Fetching current user profile...');
    const profileSnap = await getDoc(profileRef);
    
    let profilePictureCleanupError = null;
    
    // Handle profile picture cleanup if it exists
    if (profileSnap.exists()) {
      const currentData = profileSnap.data();
      
      // Check for profile picture in different possible locations
      const profilePictureUrl = currentData.photoURL || 
                               currentData.basicInfo?.photoURL;
      
      if (profilePictureUrl) {
        try {
          console.log('[OnboardingTest] Attempting to clean up profile picture...');
          
          // Extract storage path from URL if it's a Firebase Storage URL
          if (profilePictureUrl.includes('firebase')) {
            // This is a simplified approach - in a real implementation you might want
            // to store the storage path separately or parse the URL more carefully
            const pathMatch = profilePictureUrl.match(/o\/(.*?)\?/);
            if (pathMatch && pathMatch[1]) {
              const storagePath = decodeURIComponent(pathMatch[1]);
              const pictureRef = ref(storage, storagePath);
              await deleteObject(pictureRef);
              console.log('[OnboardingTest] Profile picture deleted successfully');
            }
          }
        } catch (cleanupError) {
          // Profile picture cleanup is non-blocking
          console.warn('[OnboardingTest] Profile picture cleanup failed (non-blocking):', cleanupError);
          profilePictureCleanupError = cleanupError.message;
        }
      }
    }

    // Prepare the reset data structure
    const currentBasicInfo = profileSnap.exists() ? profileSnap.data().basicInfo : {};
    
    const resetData = {
      basicInfo: {
        firstName: null,
        lastName: null,
        linkedinUrl: null,
        currentRole: null,
        companyId: null,
        companyName: null,
        location: null,
        locationData: null,
        photoURL: null,
        // Preserve email and any other persistent user info
        email: currentBasicInfo?.email || null
      },
      preferences: {
        jobSearchStatus: null
      },
      onboardingComplete: false,
      photoURL: null,
      lastUpdated: serverTimestamp()
    };

    // Update the user profile with reset data
    console.log('[OnboardingTest] Updating user profile with reset data...');
    await updateDoc(profileRef, resetData);
    
    console.log('[OnboardingTest] Data reset completed successfully');
    
    return { 
      success: true,
      profilePictureCleanupError // Include any non-blocking cleanup errors
    };
    
  } catch (error) {
    console.error('[OnboardingTest] Error clearing user onboarding data:', error);
    
    // Provide more specific error messages
    let errorMessage = 'Failed to reset onboarding data';
    
    if (error.code === 'permission-denied') {
      errorMessage = 'Permission denied - unable to reset data';
    } else if (error.code === 'not-found') {
      errorMessage = 'User profile not found';
    } else if (error.code === 'unavailable') {
      errorMessage = 'Service temporarily unavailable - please try again';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

/**
 * Validates that a user ID is provided and properly formatted
 * @param {string} userId - The user ID to validate
 * @returns {boolean} Whether the user ID is valid
 */
export const validateUserId = (userId) => {
  return userId && typeof userId === 'string' && userId.trim().length > 0;
};